// Разбор плоского текста расшифровки в реплики.
//
// Вкладка «Транскрибация» держит расшифровку готовыми фрагментами, а экран
// «Протокол» — одной строкой: там же лежит текст, вставленный руками или
// принесённый из истории. Пока рендер был у каждого свой, один экран показывал
// имена и таймкоды, а другой — сплошную серую стену. Разбор здесь один на оба.

// (1) метка (2) необязательный таймкод (3) реплика
const LINE_RE =
  /^\s*(\[[^[\]\n]{1,40}\]|[^:\n[\]]{1,80}?)[ \t]*(\[\d{1,2}:\d{2}:\d{2}[^\]\n]*\])?[ \t]*:\s(.*)$/;
const TIMECODE_ONLY_RE = /^\[?\d{1,2}:\d{2}:\d{2}/;

// Доля строк с меткой, ниже которой текст считается неразмеченным.
//
// 🔴 Одного совпадения мало. Метка здесь — «что угодно до двоеточия в первых
// сорока символах», и обычная фраза «Итого: три пункта» под это подходит. В
// размеченной расшифровке метка стоит ПОЧТИ В КАЖДОЙ строке, в прозе — в одной
// случайной. Без этого порога две случайные строки объявляли себя участниками,
// а весь остальной текст приклеивался к ним продолжением и превращался в стену
// (05.09.2026, найдено владельцем на своей записи).
//
// Треть, а не половина: реплика может переноситься на несколько строк, и тогда
// строк без метки законно больше, чем с меткой.
const LABEL_SHARE_MIN = 1 / 3;

// 🔴 Метка спикера — это имя или роль: «Менеджер», «Иван Петров»,
// «[УЧАСТНИК_1]», «Оператор (менеджер)». Больше трёх слов — это уже начало
// фразы, а не подпись: «Мы хотели бы видеть так: каждую деталь» проходило
// разбор и объявляло себя участником. Одной доли строк мало — в короткой
// расшифровке две таких фразы дают ровно треть.
const LABEL_WORDS_MAX = 3;

// Без таймкода метка — до сорока знаков, как было.
const LABEL_CHARS_MAX = 40;

// 🔴 Метка с таймкодом сразу за ней — подпись, а не фраза: «Мы хотели бы
// видеть так [00:01:02]:» прозой не пишут. Ей лимит слов не нужен: имя из
// Zoom или Толка — «Антон Долгов Первый Бит», «Елизавета Кайбазакова, Лаб ИИ,
// Питер». С лимитом в три слова такие метки отбрасывались, доля размеченных
// строк падала ниже трети, и запись встречи с разделёнными спикерами
// показывалась сплошным текстом (05.10.2026, встреча владельца в Zoom).
const looksLikeLabel = (label, timecode) =>
  Boolean(label) && !TIMECODE_ONLY_RE.test(label)
  && (Boolean(timecode)
    || (label.length <= LABEL_CHARS_MAX
      && label.split(/\s+/).filter(Boolean).length <= LABEL_WORDS_MAX));

/**
 * Текст → [{ speaker, timecode, text }].
 *
 * Возвращает null, если реплик со спикерами в тексте нет: вставленную заметку
 * или сплошной текст без разметки честнее показать как есть, чем притворяться,
 * что мы разобрали его на участников.
 */
export function parseTranscriptText(text) {
  if (!text || !text.trim()) return null;
  const fragments = [];
  let labelled = 0;
  let nonEmpty = 0;

  for (const line of text.split('\n')) {
    if (line.trim()) nonEmpty += 1;
    const match = line.match(LINE_RE);
    const label = match && match[1].trim();
    if (match && looksLikeLabel(label, match[2])) {
      labelled += 1;
      fragments.push({ speaker: label, timecode: match[2] || null, text: match[3].trim() });
      continue;
    }
    if (!line.trim()) continue;
    // Продолжение реплики (перенос строки внутри абзаца) — дописываем к прошлой.
    if (fragments.length) {
      const previous = fragments[fragments.length - 1];
      previous.text = previous.text ? `${previous.text} ${line.trim()}` : line.trim();
    } else {
      fragments.push({ speaker: null, timecode: null, text: line.trim() });
    }
  }

  // Порога по числу меток нет намеренно: расшифровка одного говорящего — это
  // одна метка на весь текст, и она законна. Отсекает долю, а не количество.
  return labelled / Math.max(nonEmpty, 1) >= LABEL_SHARE_MIN ? fragments : null;
}

/**
 * Склеивает подряд идущие реплики одного участника в один блок.
 *
 * Имя, повторённое пять раз подряд, — это шум: человек читает разговор, а не
 * список строк. Таймкод берём от первой реплики блока.
 *
 * 🔴 Блок хранит и `parts` — исходные фразы со своими таймкодами. Склейка в одну
 * строку теряла время каждой фразы, кроме первой, и абзац становился сплошным:
 * «раз выделен тайминг, фразу надо начинать с новой строки» (владелец,
 * 05.09.2026). Поле `text` оставлено прежним — им пользуется копирование и
 * выгрузка.
 */
export function groupTurns(fragments) {
  const turns = [];
  (fragments || []).forEach((fragment) => {
    const previous = turns[turns.length - 1];
    if (previous && previous.speaker && previous.speaker === fragment.speaker) {
      previous.text = `${previous.text} ${fragment.text || ''}`.trim();
      previous.parts.push({ timecode: fragment.timecode || null, text: (fragment.text || '').trim() });
      return;
    }
    turns.push({
      parts: [{ timecode: fragment.timecode || null, text: fragment.text || '' }],
      speaker: fragment.speaker || null,
      timecode: fragment.timecode || null,
      text: (fragment.text || '').trim(),
    });
  });
  return turns.filter((turn) => turn.text || turn.speaker);
}

// ── Время из таймкода ────────────────────────────────────────────────────────
//
// На экране таймкод — строка, и разбору выше этого хватает. Выгрузке в
// субтитры (`transcriptExport.js`) нужны числа: начало и конец реплики в
// секундах. Формы, которые встречаются в расшифровках:
//   «[0:00:01 - 0:00:05]» — текст Истории с 02.10, у диаризации;
//   «[00:00:07]»          — только начало (плоская расшифровка, старые записи);
//   «00:00:00.320»        — доли секунды у сервера.

const CLOCK = '\\d{1,2}:\\d{2}:\\d{2}(?:[.,]\\d+)?';
const CLOCK_RE = new RegExp(`^(\\d{1,2}):(\\d{2}):(\\d{2}(?:[.,]\\d+)?)$`);
const RANGE_RE = new RegExp(`^\\[?\\s*(${CLOCK})(?:\\s*-\\s*(${CLOCK}))?\\s*\\]?$`);
const LEADING_RE = new RegExp(`^\\s*(\\[${CLOCK}(?:\\s*-\\s*${CLOCK})?\\])\\s*:?\\s*(.*)$`);

/** «0:01:05» / «00:00:00.320» → секунды; не время — null (а не ноль). */
export function parseClock(value) {
  const match = String(value || '').trim().match(CLOCK_RE);
  if (!match) return null;
  const minutes = Number(match[2]);
  const seconds = Number(match[3].replace(',', '.'));
  if (minutes > 59 || seconds >= 60) return null;
  return Number(match[1]) * 3600 + minutes * 60 + seconds;
}

/** Таймкод реплики → { start, end } в секундах; чего нет — null. */
export function parseTimecodeRange(timecode) {
  const match = String(timecode || '').trim().match(RANGE_RE);
  if (!match) return { start: null, end: null };
  return { start: parseClock(match[1]), end: match[2] ? parseClock(match[2]) : null };
}

/**
 * Таймкод в начале строки без участника: «[00:00:07]: реплика».
 *
 * Так Ньютон пишет в Историю расшифровку без диаризации. `parseTranscriptText`
 * такую строку намеренно не считает репликой участника (таймкод — не имя),
 * поэтому время из неё достаётся здесь.
 */
export function splitLeadingTimecode(line) {
  const match = String(line || '').match(LEADING_RE);
  return match
    ? { timecode: match[1], text: match[2].trim() }
    : { timecode: null, text: String(line || '').trim() };
}
